import { useEffect, useRef } from 'react';

interface AuthLoopPreventionOptions {
    maxAttempts?: number;
    timeWindow?: number; // in milliseconds
    onLoopDetected?: () => void;
}

export const useAuthLoopPrevention = (
    isAuthenticated: boolean,
    isLoading: boolean,
    error: any,
    options: AuthLoopPreventionOptions = {}
) => {
    const {
        maxAttempts = 3,
        timeWindow = 30000, // 30 seconds
        onLoopDetected
    } = options;

    const attemptsRef = useRef<number[]>([]);
    const hasDetectedLoopRef = useRef(false);

    useEffect(() => {
        // Don't track if already detected a loop
        if (hasDetectedLoopRef.current) return;

        // Track authentication attempts
        if (!isLoading && !isAuthenticated && error) {
            const now = Date.now();
            attemptsRef.current.push(now);

            // Clean old attempts outside the time window
            attemptsRef.current = attemptsRef.current.filter(
                timestamp => now - timestamp < timeWindow
            );

            // Check if we've exceeded max attempts
            if (attemptsRef.current.length >= maxAttempts) {
                console.error('Authentication loop detected!', {
                    attempts: attemptsRef.current.length,
                    timeWindow,
                    error
                });

                hasDetectedLoopRef.current = true;
                
                if (onLoopDetected) {
                    onLoopDetected();
                }
            }
        }

        // Reset attempts on successful authentication
        if (isAuthenticated) {
            attemptsRef.current = [];
            hasDetectedLoopRef.current = false;
        }
    }, [isAuthenticated, isLoading, error, maxAttempts, timeWindow, onLoopDetected]);

    return {
        hasDetectedLoop: hasDetectedLoopRef.current,
        attemptCount: attemptsRef.current.length,
        resetLoop: () => {
            attemptsRef.current = [];
            hasDetectedLoopRef.current = false;
        }
    };
};